import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import { html } from "@polymer/polymer/lib/utils/html-tag.js";
import template from "./ns-product-action.html";
import { events, util, dateFormatter, UrlService } from "@nuskin/ns-util";
import {
  FavoritesService,
  CartService,
  OrderType,
  Product,
  ProductStatus
} from "@nuskin/ns-shop";

import "@nuskin/ns-style-elements";

/**
 * The ns-product-action should be used for
 *
 * <h1>Product Action Card</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-product-action class="demoElement"></ns-product-action>
 *   </template>
 * </demo-snippet>
 *
 */

const PENDING_FAVORITE = "pendingFavorite";

export class NsProductAction extends PolymerElement {
  constructor() {
    super();
  }

  ready() {
    super.ready();
  }

  static get is() {
    return "ns-product-action";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * Disables quantity and add to bag button if no quantity is available
       */
      disabled: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Toggles functionality and view if is base sku
       */
      isBaseSku: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Toggles functionality if logged in
       */
      isLoggedIn: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Notifies if the status call has been complete
       */
      isStatusSet: {
        type: Boolean,
        observer: "_setStatus"
      },
      /**
       * Toggles displays to be visible when on touch device
       */
      isTouchDevice: {
        type: Boolean,
        reflectToAttribute: true,
        observer: "_setTouchDevice"
      },
      /**
       * Heart icon is filled if true
       */
      isFavorite: {
        type: Boolean,
        reflectToAttribute: true,
        value: false
      },
      /**
       * Maximum amount of product allowed in quantity selector
       */
      maxQuantity: {
        type: Number,
        observer: "_setStatus",
        value: 999
      },
      /**
       * Product status message, e.g. "Out of Stock"
       */
      message: {
        type: String,
        value: ""
      },
      /**
       * Handles open state of component, tied to iron-collapse
       */
      opened: Boolean,
      /**
       * Product object
       */
      product: {
        type: Object,
        observer: "_setAttributes"
      },
      /**
       * Quantity of product selected
       */
      quantity: Number,
      /**
       * Product sku
       */
      sku: String,
      /**
       * Translations object
       */
      translations: Object,
      /**
       * Hides favorite icon
       */
      hideFavorites: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Used to show or hide exclusive message
       */
      showExclusiveMessage: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      exclusiveOfferMessage: {
        type: String,
        value: ""
      }
    };
  }

  /**
   * Adds quantity to bag, triggering toast
   */
  addToBag() {
    if (!this.disabled) {
      window.sessionStorage.setItem("nstoast-allowed", "true");
      let cntryCd = CartService.getCartProperty("cntryCd"),
        userID = null;
      if (typeof user !== "undefined" && user && user.id) userID = user.id;
      let options = {
        userId: userID,
        cntryCd: cntryCd ? cntryCd : "",
        product: new Product(this.product.toJSON()),
        qty: this.quantity,
        adr:
          !!this.product.orderTypes[OrderType.ADR] &&
          !this.product.orderTypes[OrderType.ORDER],
        isBusinessPortfolio: false,
        referrer: "productCard",
        sku: this.sku,
        domain: window.location.hostname
      };
      CartService.addProductToCart(options)
        .then(() => {
          this.maxQuantity -= options.qty;
          this._setStatus();
          events.publish(events.shop.ADD_TO_CART, options);
          events.publish(events.shop.SHOW_ADD_TO_BAG, options);
        })
        .catch(error => {
          console.error("Failed to add to cart.", error);
        });
    }
  }

  /**
   * Disables input fields
   */
  disable() {
    if (this.$.addToBag && this.$.quantitySelector) {
      this.$.addToBag.disable();
      this.$.quantitySelector.disable();
      this.disabled = true;
    }
  }

  /**
   * Enables input fields
   */
  enable() {
    if (this.$.addToBag && this.$.quantitySelector) {
      this.$.addToBag.enable();
      this.$.quantitySelector.enable();
      this.disabled = false;
    }
  }

  /**
   * Finds if product was pending favorites, or already in favorites
   */
  getFavorite() {
    let pending = JSON.parse(localStorage.getItem(PENDING_FAVORITE)) || null;
    if (this.isLoggedIn && pending && pending.sku === this.sku) {
      FavoritesService.addToFavorites(this.sku).then(() => {
        this.isFavorite = true;
      });
      localStorage.removeItem(PENDING_FAVORITE);
    } else if (this.isLoggedIn) {
      FavoritesService.isInFavorites(this.sku).then(data => {
        this.isFavorite = data;
      });
    }
  }

  /**
   * Sets attributes from product-basic
   */
  _setAttributes() {
    this.getFavorite();
    this._setStatus();
  }

  /**
   * Toggles functionality for better UX on touch devices
   */
  _setTouchDevice() {
    this.opened = this.isTouchDevice;
  }

  /**
   * Adds product to favorites, or requests authentication
   */
  setFavorite() {
    if (this.isLoggedIn) {
      this.isFavorite
        ? FavoritesService.removeFromFavorites(this.sku)
        : FavoritesService.addToFavorites(this.sku);
      this.isFavorite = !this.isFavorite;
    } else {
      localStorage.setItem(PENDING_FAVORITE, JSON.stringify({ sku: this.sku }));
      events.publish(events.authentication.REQUEST_FOR_LOGIN);
    }
  }

  /**
   * Handles different product statuses
   */
  _setStatus() {
    this.message = "";
    this.messageKey = "";

    if (this.isBaseSku || !this.isStatusSet) {
      this.disable();
      return;
    }

    if (this.product.availableQuantity > 0) {
      this.exclusiveOfferMessage = this.translations.exclusiveOfferMessage;
    } else {
      this.exclusiveOfferMessage = this.translations.exclusiveOfferUsedUpQty;
    }

    if (this.product.status === ProductStatus.ReleasedForSale) {
      if (this.maxQuantity > 0) {
        this.enable();
        this.messageKey = "releasedForSale";

        if (
          this.product.backOrderDate &&
          this.translations.backOrderedWithDate
        ) {
          let locale = UrlService.getLocale();
          this.message = this.translations.backOrderedWithDate.replace(
            "%date%",
            dateFormatter.getCountryFormattedDate(
              this.product.backOrderDate,
              locale
            )
          );
          this.messageKey = "backOrderedWithDate";
        }
      } else {
        this.disable();

        if (!this.product.isExclusive) {
          if (this.product.availableQuantity > 0) {
            this.message = this.translations.reachedPurchaseLimit;
            this.messageKey = "reachedPurchaseLimit";
          } else {
            this.message = this.translations.outOfStock;
            this.messageKey = "outOfStock";
          }
        }
      }
    } else {
      this.disable();

      if (this.product.status === ProductStatus.Discontinued) {
        this.message = this.translations.discontinued;
        this.messageKey = "discontinued";
      } else if (this.product.status === ProductStatus.NotReleasedForSale) {
        this.message = this.translations.notReleasedForSale;
        this.messageKey = "notReleasedForSale";
      } else if (this.product.status === ProductStatus.StopStatus) {
        this.message = this.translations.stopStatus;
        this.messageKey = "stopStatus";
      }
    }
  }

  /**
   * Redirects to the product detail page with the associated sku
   * @private
   */
  goToDetails() {
    events.publish(events.shop.GO_TO_PDP, { sku: this.sku });
  }
}
customElements.define(NsProductAction.is, NsProductAction);
